// src/services/tables/positionEligibilityColumns.js
import React from 'react';
import { Shield, AlertTriangle, Info } from 'lucide-react';
import { dynastyTheme } from '../colorService';
import InjuryBadge from '../../components/InjuryBadge';
import PlayerLink from '../../components/PlayerLink';

/**
 * Get default position eligibility based on MLB position
 * Matches backend position_utils.py POSITION_ELIGIBILITY mapping
 */
const getDefaultEligibility = (mlbPosition) => {
  if (!mlbPosition) return [];

  const pos = mlbPosition.toUpperCase();

  const eligibility = {
    'C': ['C', 'UTIL'],
    '1B': ['1B', 'CI', 'UTIL'],
    '2B': ['2B', 'MI', 'UTIL'],
    '3B': ['3B', 'CI', 'UTIL'],
    'SS': ['SS', 'MI', 'UTIL'],
    'LF': ['OF', 'UTIL'],
    'CF': ['OF', 'UTIL'],
    'RF': ['OF', 'UTIL'],
    'OF': ['OF', 'UTIL'],
    'SP': ['P'],
    'RP': ['P'],
    'P': ['P'],
    'DH': ['UTIL']
  };

  return eligibility[pos] || [];
};

/**
 * Check if player is eligible for their current slot
 */
const isEligibleForSlot = (slot, mlbPosition, customEligibility) => {
  if (!slot || slot === 'BENCH') return true;

  // Extract slot type (remove index suffix)
  const slotType = slot.split('_')[0];

  // Get effective eligibility
  const eligibility = customEligibility || getDefaultEligibility(mlbPosition);

  return eligibility.includes(slotType);
};

/**
 * Create position eligibility table columns
 * Reads canonical player structure: ids.league_player, info.*, league_context.roster.*
 */
export const createPositionEligibilityColumns = ({
  onEditPlayer,
  league
}) => {
  return [
    // Player Name
    {
      key: 'info.full_name',
      title: 'Player',
      width: 180,
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <PlayerLink
            mlbId={row.ids?.mlb}
            name={row.info.full_name || '-'}
            style={{ color: '#f9fafb' }}
          />
          <InjuryBadge mlbId={row.ids?.mlb} />
          {row.league_context.roster.custom_eligibility && (
            <Shield
              className={`w-4 h-4 ${dynastyTheme.classes.text.primary}`}
              title="Has custom eligibility override"
            />
          )}
        </div>
      ),
      sortValue: (row) => row.info.full_name || ''
    },

    // MLB Position
    {
      key: 'info.position',
      title: 'MLB Pos',
      width: 80,
      sortable: true,
      render: (value, row) => (
        <span className={dynastyTheme.classes.text.neutralLight}>
          {row.info.position || '-'}
        </span>
      )
    },

    // Current Slot
    {
      key: 'league_context.roster.position',
      title: 'Current Slot',
      width: 120,
      sortable: true,
      render: (value, row) => {
        const currentSlot = row.league_context.roster.position;
        const hasWarning = !isEligibleForSlot(
          currentSlot,
          row.info.position,
          row.league_context.roster.effective_eligibility || row.league_context.roster.custom_eligibility
        );

        return (
          <div className="flex items-center gap-2">
            <span className={dynastyTheme.classes.text.white}>
              {currentSlot || 'BENCH'}
            </span>
            {hasWarning && (
              <AlertTriangle
                className="w-4 h-4 text-amber-400"
                title="Player may not be eligible for this slot"
              />
            )}
          </div>
        );
      }
    },

    // Default Eligibility
    {
      key: 'default_eligibility',
      title: 'Default Eligible',
      width: 200,
      render: (value, row) => {
        const defaultElig = getDefaultEligibility(row.info.position);
        if (defaultElig.length === 0) {
          return (
            <span className={dynastyTheme.classes.text.neutralDark}>
              -
            </span>
          );
        }
        return (
          <div className="flex flex-wrap gap-1">
            {defaultElig.map(pos => (
              <span
                key={pos}
                className={`px-2 py-1 text-xs rounded ${dynastyTheme.components.badge.neutral}`}
              >
                {pos}
              </span>
            ))}
          </div>
        );
      }
    },

    // Auto Eligibility (from games played — computed by daily worker)
    // Shows ALL positions with game counts. Green = crossed threshold, gray = progress.
    {
      key: 'league_context.roster.auto_eligibility_games',
      title: 'Games by Position',
      width: 250,
      render: (value, row) => {
        const auto = row.league_context?.roster?.auto_eligibility || [];
        const games = row.league_context?.roster?.auto_eligibility_games || {};
        const positions = Object.keys(games);
        if (positions.length === 0) {
          return (
            <span className={dynastyTheme.classes.text.neutralDark}>
              —
            </span>
          );
        }
        // Sort by games descending
        const sorted = positions.sort((a, b) => (games[b] || 0) - (games[a] || 0));
        return (
          <div className="flex flex-wrap gap-1">
            {sorted.map(pos => {
              const isEligible = auto.includes(pos);
              return (
                <span
                  key={pos}
                  className={`px-2 py-1 text-xs rounded border ${
                    isEligible
                      ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      : 'bg-neutral-700/30 text-neutral-400 border-neutral-600/30'
                  }`}
                  title={isEligible ? `Eligible — ${games[pos]} games` : `${games[pos]} games (below threshold)`}
                >
                  {pos} ({games[pos]}G)
                </span>
              );
            })}
          </div>
        );
      }
    },

    // Custom Eligibility (Commissioner Override)
    {
      key: 'league_context.roster.custom_eligibility',
      title: 'Custom Eligible',
      width: 200,
      render: (value, row) => {
        const custom = row.league_context.roster.custom_eligibility;
        if (!custom || custom.length === 0) {
          return (
            <span className={dynastyTheme.classes.text.neutralDark}>
              None
            </span>
          );
        }

        return (
          <div className="flex flex-wrap gap-1">
            {custom.map(pos => (
              <span
                key={pos}
                className={`px-2 py-1 text-xs rounded ${dynastyTheme.components.badge.primary}`}
              >
                {pos}
              </span>
            ))}
          </div>
        );
      }
    },

    // Commissioner Notes
    {
      key: 'league_context.roster.position_override_notes',
      title: 'Notes',
      width: 250,
      render: (value, row) => {
        const notes = row.league_context.roster.position_override_notes;
        if (!notes) {
          return (
            <span className={dynastyTheme.classes.text.neutralDark}>
              -
            </span>
          );
        }

        return (
          <div className="flex items-start gap-2">
            <Info className={`w-4 h-4 mt-0.5 ${dynastyTheme.classes.text.info} flex-shrink-0`} />
            <span className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
              {notes}
            </span>
          </div>
        );
      }
    },

    // Actions
    {
      key: 'actions',
      title: 'Actions',
      width: 100,
      sortable: false,
      render: (value, row) => (
        <button
          onClick={() => onEditPlayer(row)}
          className={dynastyTheme.utils.getComponent('button', 'secondary', 'sm')}
        >
          Edit
        </button>
      )
    }
  ];
};
